/**
 * Lazy-loaded Sentry wrapper
 *
 * This module provides a lazy-loading wrapper for Sentry to reduce initial bundle size.
 * Sentry with replay and tracing integrations is ~100-150KB.
 *
 * Strategy:
 * - Queue any errors that occur before Sentry is fully loaded: explicit
 *   captureException/captureMessage calls, and (buffered by listeners that
 *   stand in for the SDK's own handlers) uncaught errors, unhandled
 *   rejections and console.error calls
 * - Load the full Sentry SDK after the first map view, so it doesn't compete
 *   with the map for the network and the main thread
 * - Provide the same API so existing code continues to work
 */

import { APP_VERSION } from "utils/appVersion";
import { whenFirstMapView } from "utils/firstMapView";

let sentryModule = null;
let isInitialized = false;
let isLoading = false;
let loadingPromise = null;

// Queue for errors captured before Sentry loads
const errorQueue = [];
// True when initSentryLazy decided not to run Sentry at all (localhost): the
// capture functions are then no-ops instead of queueing forever and
// downloading the SDK chunk for nothing.
let optedOut = false;

// Sentry configuration (set during init, used when SDK loads)
let sentryConfig = null;

// User context requested before the SDK finished loading (auth usually
// resolves within the same second the SDK is still downloading); applied on
// init so events carry the user id. `undefined` = never set.
let pendingUser;

const STALE_CHUNK_RE = /Unexpected token '?<|expected expression, got '?</;
const CHUNK_FRAME_RE = /\/static\/js\/.*\.chunk\.js/;
// React reconciliation errors thrown when something outside React rewrites
// the DOM. On a machine-translated page (Google Translate wraps text nodes in
// <font>) they are not app bugs; Translate marks <html> with translated-*.
const TRANSLATED_DOM_ERROR_RE =
  /Failed to execute '(removeChild|insertBefore)' on 'Node'/;
// Our code is served from /static/js/. In-page translation injects scripts the
// browser attributes to the document URL, so a stack with frames but none of
// them ours did not come from this app.
const FIRST_PARTY_FRAME_RE = /\/static\/js\//;

/**
 * True when every exception that HAS a stack has no frame from our bundle.
 *
 * Events with no stack at all return false: console-captured errors arrive
 * frameless and their message is usually ours, so they must keep reporting.
 */
function hasNoFirstPartyFrame(values) {
  const framed = values.filter((v) => (v?.stacktrace?.frames || []).length > 0);
  if (framed.length === 0) return false;
  return framed.every((v) =>
    v.stacktrace.frames.every(
      (f) => !FIRST_PARTY_FRAME_RE.test(f?.filename || ""),
    ),
  );
}

export function isPageTranslated(doc = globalThis.document) {
  try {
    const classList = doc?.documentElement?.classList;
    return Boolean(
      classList &&
      (classList.contains("translated-ltr") ||
        classList.contains("translated-rtl")),
    );
  } catch (e) {
    return false;
  }
}

/**
 * beforeSend: returns the event to send, or null to drop it. Exported so the
 * filtering rules are unit-testable without booting the SDK.
 *
 * - Drops the stale-bundle chunk SyntaxError ("Unexpected token '<'"): after
 *   a deploy an old client lazy-loads a chunk whose hash is gone, the SPA
 *   rewrite returns index.html, and the browser parses HTML as JS. index.js
 *   already auto-recovers with a reload, so the event is pure noise. Only
 *   dropped when a stack frame points at a hashed /static/js/*.chunk.js, so
 *   genuine first-party syntax errors are still reported.
 * - On a machine-translated page, tags the event and drops the React DOM
 *   NotFoundErrors caused by the translation (Sentry EARTHMAP-FRONTEND-47S,
 *   -47Y: ~200 events / 2 weeks, all non-English locales).
 * - On a machine-translated page, also drops errors thrown entirely outside
 *   our bundle. Sentry EARTHMAP-FRONTEND-4BY was 15 unhandled "Maximum call
 *   stack size exceeded" events from one in-app-browser session, every frame
 *   attributed to the document URL — which serves no inline script — and none
 *   to /static/js/: the translation layer recursing through the DOM it had
 *   just rewritten. Frameless events are kept, so this only silences stacks
 *   we can see are not ours.
 */
export function filterEvent(event, { translated = isPageTranslated() } = {}) {
  try {
    const values = event?.exception?.values || [];
    const staleChunk = values.some((v) => {
      const msg = `${v.type || ""}: ${v.value || ""}`;
      const fromChunk = (v?.stacktrace?.frames || []).some(
        (f) =>
          typeof f.filename === "string" && CHUNK_FRAME_RE.test(f.filename),
      );
      return STALE_CHUNK_RE.test(msg) && fromChunk;
    });
    if (staleChunk) return null;

    if (translated) {
      event.tags = { ...(event.tags || {}), page_translated: true };
      if (values.some((v) => TRANSLATED_DOM_ERROR_RE.test(v.value || ""))) {
        return null;
      }
      if (hasNoFirstPartyFrame(values)) {
        return null;
      }
    }
  } catch (e) {
    // never let beforeSend throw
  }
  return event;
}

/**
 * Load the Sentry SDK dynamically
 */
async function loadSentry() {
  if (sentryModule) return sentryModule;
  if (loadingPromise) return loadingPromise;

  isLoading = true;
  loadingPromise = import("@sentry/react")
    .then((sentry) => {
      sentryModule = sentry;
      isLoading = false;
      loadingPromise = null;
      return sentryModule;
    })
    .catch((error) => {
      console.error("Failed to load Sentry:", error);
      isLoading = false;
      loadingPromise = null;
      return null;
    });

  return loadingPromise;
}

// Until Sentry.init installs its global handlers and console instrumentation,
// these stand in for them and queue what they see (replayed after init). The
// SDK starts after the first map view, many seconds into a slow load, so
// startup errors would otherwise go unreported.
let earlyCaptureCleanup = null;

function formatConsoleArgs(args) {
  return args
    .map((arg) => {
      if (typeof arg === "string") return arg;
      try {
        return JSON.stringify(arg);
      } catch (e) {
        return String(arg);
      }
    })
    .join(" ");
}

function isStaleChunkErrorEvent(event) {
  try {
    const error = event.error;
    const messages = [
      event.message || "",
      error ? `${error.name || ""}: ${error.message || ""}` : "",
    ];
    const files = [event.filename || "", (error && error.stack) || ""];
    return (
      messages.some((m) => STALE_CHUNK_RE.test(m)) &&
      files.some((f) => CHUNK_FRAME_RE.test(f))
    );
  } catch (e) {
    return false;
  }
}

function startEarlyCapture() {
  if (earlyCaptureCleanup) return;
  const onError = (event) => {
    // The stale-deploy chunk parse error has no stack: the SDK's handler
    // builds its one frame from event.filename and filterEvent drops it on
    // that frame. Apply the same rule here so it is not queued and replayed.
    if (isStaleChunkErrorEvent(event)) return;
    if (event.error) {
      enqueue({ type: "exception", args: [event.error] });
    } else if (event.message) {
      enqueue({ type: "message", args: [event.message, { level: "error" }] });
    }
  };
  const onRejection = (event) => {
    enqueue({ type: "exception", args: [event.reason] });
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);

  // Mirrors captureConsoleIntegration({ levels: ["error"] }).
  const originalConsoleError = console.error;
  const bufferingConsoleError = function bufferingConsoleError(...args) {
    const error = args.find((arg) => arg instanceof Error);
    if (error) {
      enqueue({
        type: "exception",
        args: [error, { level: "error", extra: { arguments: args } }],
      });
    } else {
      enqueue({
        type: "message",
        args: [
          formatConsoleArgs(args),
          { level: "error", extra: { arguments: args } },
        ],
      });
    }
    return originalConsoleError.apply(this, args);
  };
  console.error = bufferingConsoleError;

  earlyCaptureCleanup = () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    // Hand console.error back only if nobody wrapped it after us.
    if (console.error === bufferingConsoleError) {
      console.error = originalConsoleError;
    }
    earlyCaptureCleanup = null;
  };
}

function stopEarlyCapture() {
  if (earlyCaptureCleanup) earlyCaptureCleanup();
}

/**
 * Initialize Sentry lazily
 * Call this early in your app - it buffers early errors and loads Sentry once
 * the first map view is up (see utils/firstMapView)
 */
export function initSentryLazy() {
  // Don't initialize on localhost
  if (window.location.hostname === "localhost") {
    optedOut = true;
    return;
  }

  // Store config for when Sentry loads
  sentryConfig = {
    dsn: process.env.REACT_APP_SENTRY_CLIENT_ID,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 0.5,
    debug: false,
    release: APP_VERSION,
    ignoreErrors: [
      "Non-Error exception captured",
      "Non-Error promise rejection captured",
      // Thrown by firebase-auth's IndexedDB persistence when the tab is
      // hidden/closing while it persists the user (Chrome tears down
      // IndexedDB on hidden pages); the SDK retries on page show, so the
      // event is transient noise. Common on dev hot reloads.
      "Database is closing/hidden",
    ],
  };

  startEarlyCapture();

  // Load Sentry once the first map view is up, then at the next idle moment.
  const scheduleLoad = (cb) =>
    whenFirstMapView().then(() => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(cb, { timeout: 2000 });
      } else {
        setTimeout(cb, 0);
      }
    });

  scheduleLoad(async () => {
    try {
      const Sentry = await loadSentry();
      if (Sentry && sentryConfig) {
        // Give console.error back before the SDK instruments it.
        stopEarlyCapture();
        Sentry.init({
          ...sentryConfig,
          beforeSend: filterEvent,
          integrations: [
            Sentry.browserTracingIntegration({
              // Skip span creation for tile URLs (Google Earth Engine map tiles,
              // Google Maps tile servers). One request per visible tile is the
              // normal pattern and Sentry's N+1 detector flags it as a bug.
              shouldCreateSpanForRequest: (url) => {
                if (
                  /earthengine\.googleapis\.com\/.*\/tiles\//.test(url) ||
                  /googleapis\.com\/.*\/maps\/.*\/tiles\//.test(url) ||
                  /khms\d*\.google(apis)?\.com/.test(url) ||
                  /mts\d*\.google(apis)?\.com/.test(url)
                ) {
                  return false;
                }
                return true;
              },
            }),
            Sentry.captureConsoleIntegration({
              levels: ["error"],
            }),
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            }),
          ],
        });
        isInitialized = true;
        if (pendingUser !== undefined) {
          Sentry.setUser(pendingUser);
        }

        // Process queued errors
        while (errorQueue.length > 0) {
          const { type, args } = errorQueue.shift();
          if (type === "exception") {
            Sentry.captureException(...args);
          } else if (type === "message") {
            Sentry.captureMessage(...args);
          }
        }
      } else {
        // SDK unavailable (offline, blocked): stop buffering for good rather
        // than leave console.error wrapped and the listeners attached.
        stopEarlyCapture();
      }
    } catch (error) {
      stopEarlyCapture();
      console.error("Failed to initialize Sentry:", error);
    }
  });
}

/**
 * Capture an exception - queues if Sentry not yet loaded
 */
export function captureException(exception, captureContext) {
  if (optedOut) return;
  if (sentryModule && isInitialized) {
    sentryModule.captureException(exception, captureContext);
  } else {
    // Queue for later
    enqueue({ type: "exception", args: [exception, captureContext] });
    // Try to load Sentry if not already loading
    if (!isLoading && !sentryModule) {
      loadSentry();
    }
  }
}

// Bounded queue: when the SDK never initialises (opted out, or Sentry.init
// threw) every captured error — with its `extra` payloads — used to be kept for
// the life of the page. Oldest entries are dropped past the cap.
const MAX_QUEUED_EVENTS = 50;
function enqueue(entry) {
  if (errorQueue.length >= MAX_QUEUED_EVENTS) errorQueue.shift();
  errorQueue.push(entry);
}

/**
 * Capture a message - queues if Sentry not yet loaded
 */
export function captureMessage(message, captureContext) {
  if (optedOut) return;
  if (sentryModule && isInitialized) {
    sentryModule.captureMessage(message, captureContext);
  } else {
    enqueue({ type: "message", args: [message, captureContext] });
    if (!isLoading && !sentryModule) {
      loadSentry();
    }
  }
}

/**
 * Set user context - remembered and applied once Sentry has loaded
 */
export function setUser(user) {
  pendingUser = user;
  if (sentryModule && isInitialized) {
    sentryModule.setUser(user);
  }
}

/**
 * Set extra context - no-op if Sentry not loaded
 */
export function setExtra(key, extra) {
  if (sentryModule && isInitialized) {
    sentryModule.setExtra(key, extra);
  }
}

/**
 * Set tag - no-op if Sentry not loaded
 */
export function setTag(key, value) {
  if (sentryModule && isInitialized) {
    sentryModule.setTag(key, value);
  }
}

/**
 * Add breadcrumb - no-op if Sentry not loaded
 */
export function addBreadcrumb(breadcrumb) {
  if (sentryModule && isInitialized) {
    sentryModule.addBreadcrumb(breadcrumb);
  }
}

/**
 * With scope - executes callback immediately, Sentry operations may be no-op
 */
export function withScope(callback) {
  if (sentryModule && isInitialized) {
    sentryModule.withScope(callback);
  } else {
    // Execute callback with a mock scope
    callback({
      setExtra: () => {},
      setTag: () => {},
      setUser: () => {},
      setLevel: () => {},
    });
  }
}

/**
 * Check if Sentry is loaded and initialized
 */
export function isSentryReady() {
  return isInitialized;
}

/**
 * Get the Sentry module directly (for advanced use cases)
 * Returns null if not yet loaded
 */
export function getSentryModule() {
  return sentryModule;
}

// Default export for backwards compatibility
export default {
  initSentryLazy,
  captureException,
  captureMessage,
  setUser,
  setExtra,
  setTag,
  addBreadcrumb,
  withScope,
  isSentryReady,
  getSentryModule,
};
